import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Alert,
  Tooltip,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  FormControlLabel,
  Switch,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Collapse,
  Stack,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { createAgent, updateAgent, listIndexes, listTools, listKeys } from "../../api/agentApi";
import { useLocation, useNavigate } from "react-router-dom";
import AutocompleteInput from "./Options/AutocompleteInput";
import AutocompleteInputName from "./Options/AutocompleteInputName";
import {
  CATEGORY_OPTIONS,
  getRequirementLabel,
  getToolCatalogMetadata,
} from "../../toolCatalogMetadata";

import {
  roleOptions,
  intentOptions,
  llmOptions,
  createSteps,
  editSteps,
} from "./Constants";
import { useUser } from "../UserContext";
import { PERMISSIONS } from "../../permissions";
import { getAgentCreatedDetailState } from "../../agentNavigation";

interface Props {
  onAgentCreated?: (agentId: string) => void;
}

const extractSelectedToolKey = (tool: any): string | null => {
  const requiredKeyEntry =
    Array.isArray(tool?.required_keys) && tool.required_keys.length > 0
      ? tool.required_keys[0]
      : null;
  const requiredKeyName = requiredKeyEntry ? Object.keys(requiredKeyEntry)[0] : null;
  return requiredKeyName ? requiredKeyEntry[requiredKeyName] : tool?.selectedKey ?? null;
};

const initialSelectedTools = (agentCard: any): any[] => {
  if (!agentCard) return [];
  return Array.isArray(agentCard.tools) ? agentCard.tools : [];
};

const StepperWizard: React.FC<Props> = (props) => {
  const navigate = useNavigate();
  const { user, can } = useUser();
  const location = useLocation();
  const editingAgentCard = location.state?.agentCard;
  // Determine if this is an update (edit) or create
  // If editing, get agentCard from location.state
  const isEdit = !!editingAgentCard;
  const steps = isEdit ? editSteps : createSteps;
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<boolean[]>(
    steps.map(() => false)
  );

  const currentStepConfig = steps[activeStep];
  
  
  // Users without agent update permission can only review an existing agent.
  const isNonAdmin = !can?.(PERMISSIONS.AGENTS_UPDATE);
  
  // If non-admin and editing, skip to step 5 (review step)
  useEffect(() => {
    if (isEdit && isNonAdmin) {
      setActiveStep(4); // Step 5 is index 4
      // Mark all previous steps as completed
      setCompletedSteps([true, true, true, true, false]);
    }
  }, [isEdit, isNonAdmin]);

  const handleNext = () => {
    if (activeStep < steps.length - 1 && isCurrentStepComplete()) {
      const newCompletedSteps = [...completedSteps];
      newCompletedSteps[activeStep] = true;
      setCompletedSteps(newCompletedSteps);

      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    // Prevent going back if non-admin in edit mode
    if (isEdit && isNonAdmin) {
      return;
    }
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  // --------------------------------------------------

  const [form, setForm] = useState(() => {
    if (editingAgentCard) {
      return {
        role: editingAgentCard.role || "",
        name: editingAgentCard.name || "",
        display_name: editingAgentCard.display_name || "",
        description: editingAgentCard.description || "",
        tools: editingAgentCard.tools || [],
        llm: editingAgentCard.llm || "openai",
        temperature: editingAgentCard.llm_settings?.temperature ?? 0,
        max_tokens: editingAgentCard.llm_settings?.max_tokens ?? 4096,
        status: editingAgentCard.status || "Creating",
      };
    }
    return {
      role: "",
      name: "",
      display_name: "",
      description: "",
      tools: [],
      llm: "openai",
      temperature: 0,
      max_tokens: 4096,
      status: "Creating",
    };
  });

  // Check if current step is complete
  const isCurrentStepComplete = (): boolean => {
    switch (activeStep) {
      case 0:
        return !!form.role;
      case 1:
        return !!form.name && !!form.description;
      case 2:
        return selectedTools.every(
          (tool: any) =>
            !Array.isArray(tool.keys_schema) ||
            tool.keys_schema.length === 0 ||
            !!tool.selectedKey
        );
      case 3:
      case 4:
        return true;
      default:
        return false;
    }
  };

  // Also prepopulate selectedTools if editing
  const [selectedTools, setSelectedTools] = useState(() =>
    initialSelectedTools(editingAgentCard).length > 0
      ? initialSelectedTools(editingAgentCard).map((t: any) => ({
          ...t,
          paramValues: {},
          selectedKey: extractSelectedToolKey(t),
          // Ensure params is always an array for editing
          params: t.parameters
            ? t.parameters.map((p: any) => ({
                name: p.name || "",
                type: p.type || "string",
                description: p.description || "",
              }))
            : t.params || [],
        }))
      : []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [manualMode, setManualMode] = useState(false);
  const [manualJsonValue, setManualJsonValue] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [success, setSuccess] = useState("");
  
  // Tool catalog UI state
  const [showCatalog, setShowCatalog] = useState(true); // Toggle catalog section visibility
  const [toolSearchQuery, setToolSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [toolDetailOpen, setToolDetailOpen] = useState(false);
  const [selectedToolDetail, setSelectedToolDetail] = useState<any>(null);

  const [docIndexes, setDocIndexes] = useState([]);
  const [codeIndexes, setCodeIndexes] = useState([]);
  const [selectedDocIndex, setSelectedDocIndex] = useState(null);
  const [selectedKnowledgeSpaceIds, setSelectedKnowledgeSpaceIds] = useState<string[]>([]);
  const [selectedCodeIndex, setSelectedCodeIndex] = useState(null);
  const [catalogTools, setCatalogTools] = useState<any[]>([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [toolsHydrated, setToolsHydrated] = useState(false);
  const [availableKeys, setAvailableKeys] = useState<any[]>([]);

  useEffect(() => {
    async function fetchIndexes() {
      try {
        const docRes = await listIndexes("doc");
        setDocIndexes(docRes.indexes || []);
        const codeRes = await listIndexes("code");
        setCodeIndexes(codeRes.indexes || []);
      } catch (e) {
        // ignore errors for now
      }
    }
    fetchIndexes();
  }, []);

  useEffect(() => {
    async function fetchKeys() {
      try {
        const res = await listKeys();
        setAvailableKeys(res.keys || []);
      } catch (e) {
        setAvailableKeys([]);
      }
    }
    fetchKeys();
  }, []);

  // Fetch tools from catalog
  useEffect(() => {
    async function fetchTools() {
      setLoadingTools(true);
      try {
        // Fetch all public tools, then filter for published on frontend
        const toolsRes = await listTools();
        
        // Filter to only show published tools (production-ready)
        const productionTools = toolsRes.filter((tool: any) => 
          tool.status === 'published'
        );

        
        // Transform catalog tools to match the expected format
        const transformedTools = productionTools.map((tool: any) => {
          // Handle new JSON Schema format for parameters 
          let params = [];  
          if (tool.parameters && typeof tool.parameters === 'object') {
            if (Array.isArray(tool.parameters)) {
              // Old array format (backward compatibility)
              params = tool.parameters.map((p: any) => ({
                name: p.name || "",
                type: p.type || "string",
                description: p.description || "",
                required: p.required || false,
                default: p.default,
              }));
            } else if (tool.parameters.properties) {
              // New JSON Schema object format
              const required = tool.parameters.required || [];
              params = Object.entries(tool.parameters.properties).map(([name, prop]: [string, any]) => ({
                name: name,
                type: prop.type || "string",
                description: prop.description || "",
                required: required.includes(name),
                default: prop.default,
              }));
            }
          }
          const metadata = getToolCatalogMetadata(tool);

          // Pass through setup and key metadata for readiness guidance.
          return {
            name: tool.name,
            display_name: tool.display_name || tool.name,
            description: tool.description,
            params: params,
            invocation: tool.invocation_config || { type: tool.invocation_type || "python_function" },
            category: tool.category,
            canonical_category: metadata.canonicalCategory,
            collection: metadata.collection,
            capability_tags: metadata.capabilityTags,
            provider: metadata.provider,
            provider_label: metadata.providerLabel,
            auth_required: tool.auth_required,
            setup_requirements: metadata.setupRequirements,
            tool_id: tool.id, // Store the catalog tool ID
            version: tool.version,
            keys_schema: tool.keys_schema || [],
          };
        });
        setCatalogTools(transformedTools);
      } catch (e) {
        console.error("Failed to fetch tools from catalog:", e);
      } finally {
        setLoadingTools(false);
      }
    }
    fetchTools();
  }, []);

  useEffect(() => {
    if (!isEdit || toolsHydrated || catalogTools.length === 0) return;

    setSelectedTools((prev: any[]) =>
      prev.map((tool: any) => {
        const catalogMatch = catalogTools.find(
          (catalogTool: any) =>
            (tool.tool_id && catalogTool.tool_id === tool.tool_id) ||
            (!tool.tool_id && catalogTool.name === tool.name)
        );

        const hydratedKeysSchema =
          tool.keys_schema && tool.keys_schema.length > 0
            ? tool.keys_schema
            : catalogMatch?.keys_schema || [];

        return {
          ...tool,
          keys_schema: hydratedKeysSchema,
          provider: tool.provider || catalogMatch?.provider,
          provider_label: tool.provider_label || catalogMatch?.provider_label,
          canonical_category: tool.canonical_category || catalogMatch?.canonical_category,
          collection: tool.collection || catalogMatch?.collection,
          capability_tags: tool.capability_tags || catalogMatch?.capability_tags,
          setup_requirements: tool.setup_requirements || catalogMatch?.setup_requirements || [],
          selectedKey: tool.selectedKey || extractSelectedToolKey(tool),
        };
      })
    );

    setToolsHydrated(true);
  }, [catalogTools, isEdit, toolsHydrated]);
  // Prepopulate selected knowledge spaces and code index in Edit mode.
  useEffect(() => {
    if (isEdit && editingAgentCard) {
      const knowledgeSpaces = Array.isArray(editingAgentCard.knowledge_spaces)
        ? editingAgentCard.knowledge_spaces
        : [];
      const ragEntries = Array.isArray(editingAgentCard.rag) ? editingAgentCard.rag : [];
      const docIds = [
        ...knowledgeSpaces
          .map((entry:any) => entry.knowledge_space_id || entry.index_id || entry.id || entry)
          .filter(Boolean),
        ...ragEntries
          .filter((idx:any) => idx.rag_type === "doc")
          .map((idx:any) => idx.id || idx.index_id)
          .filter(Boolean),
      ];
      const uniqueDocIds = Array.from(new Set(docIds.map((id:any) => String(id))));
      setSelectedKnowledgeSpaceIds(uniqueDocIds);
      setSelectedDocIndex(uniqueDocIds[0] || null);

      const codeIdx = ragEntries.find((idx:any) => idx.rag_type === "code");
      if (codeIdx && codeIdx.id) setSelectedCodeIndex(codeIdx.id);
    }
  }, [isEdit, editingAgentCard]);

  const reviewTools = selectedTools;
 
  const formatToolKeyForAgent = (tool: any) => {
    if (
      tool.tool_id &&
      Array.isArray(tool.keys_schema) &&
      tool.keys_schema.length > 0 &&
      tool.selectedKey
    ) {
      const keyName = tool.keys_schema[0]?.name;
      return keyName ? [{ [keyName]: tool.selectedKey }] : undefined;
    }
    return undefined;
  };

  // Convert tools to schema format (parameters array, not paramValues)
  const toolsForCard = reviewTools.map((tool: any) => {
    const requiredKeys = formatToolKeyForAgent(tool);
    return {
      name: tool.name,
      description: tool.description,
      parameters: (tool.params || []).map((param: any) => ({
        name: param.name,
        type: param.type,
        description: param.description || "",
      })),
      invocation:
        tool.invocation && tool.invocation.type
          ? { type: tool.invocation.type }
          : { type: "python_function" },
      // Include catalog tool metadata if present
      ...(tool.tool_id && { tool_id: tool.tool_id }),
      ...(tool.version && { version: tool.version }),
      ...(tool.provider && { provider: tool.provider }),
      ...(tool.provider_label && { provider_label: tool.provider_label }),
      ...(tool.canonical_category && { canonical_category: tool.canonical_category }),
      ...(tool.collection && { collection: tool.collection }),
      ...(tool.capability_tags && { capability_tags: tool.capability_tags }),
      ...(tool.setup_requirements && { setup_requirements: tool.setup_requirements }),
      ...(requiredKeys && { required_keys: requiredKeys }),
    };
  });
  // Compose llm_settings
  const llm_settings = {
    temperature: form.temperature,
    max_tokens: form.max_tokens,
  };
  // Compose agent card
  const agentCard = {
    name: form.name,
    description: form.description,
    llm: form.llm,
    llm_settings,
    role: form.role,
    tools: toolsForCard,
    // Optionally add inputs/output if you have those steps
    owner: user?.username || "Unknown",
    ...(form.display_name && { display_name: form.display_name }), // Include display_name if provided
  };
  // When agent is created, update status to "created"
  const handleCreateAgent = async () => {
    setLoading(true);
    setError("");
    setSuccessMsg("");
    setSuccess("");
    try {
      let result;
      if (isEdit && editingAgentCard) {
        // Update agent: explicit null means the user removed the collection.
        const updatePayload: any = { ...agentCard };
        updatePayload.document_index_id = selectedKnowledgeSpaceIds[0] || selectedDocIndex || null;
        updatePayload.knowledge_space_ids = selectedKnowledgeSpaceIds;
        if (selectedCodeIndex) updatePayload.code_index_id = selectedCodeIndex;
        result = await updateAgent(editingAgentCard.agent_id, updatePayload);
      } else {
        // Create agent
        result = await createAgent(
          agentCard,
          selectedKnowledgeSpaceIds[0] || selectedDocIndex,
          selectedCodeIndex,
          selectedKnowledgeSpaceIds
        );
      }
      if (result.status === "success" || result.status === "started") {
        const kbDetailUrl = `/kb/${result.type === "doc" ? "doc" : "code"}/${
          result.task_id
        }`;
        if (isEdit) {
          navigate(`/agent/${result.agent_id}`);
        } else {
          navigate(`/agent/${result.agent_id}`, {
            state: getAgentCreatedDetailState(result.agent_id),
          });
        }
        if (props.onAgentCreated) props.onAgentCreated(result.agent_id);
      } else {
        setError(result.message || "Failed to save agent.");
      }
    } catch (e: any) {
      setError(e.message || "Failed to save agent.");
    } finally {
      setLoading(false);
      
    }
  };
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Box sx={{ display: "flex", alignItems: "flex-end", mb: 2 }}></Box>
            {form.role && (
              <Box sx={{ mt: 2, mb: 1, ml: 1 }}>
                <Typography variant="body1" color="text.secondary">
                  Great! You're creating an agent as a <b>{form.role}</b>.
                </Typography>
              </Box>
            )}
            <AutocompleteInput
              options={roleOptions}
              setForm={setForm}
              form={form}
              value={form.role}
              label={"Agent Role"}
              placeholder="Select or type custom value..."
              helperText="Choose from suggestions or enter your own"
            />
          </Box>
        );
      case 1:
        const intentOptionsFromRole = intentOptions[form.role] || [];
        // const options:any = [];
        return (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Role: <b>{form.role}</b>
              </Typography>
            </Box>
            {/* <Box sx={{ display: "flex", alignItems: "flex-end", mb: 2 }}> */}
            <Box sx={{ mr: 2, mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                🗨️ I want to create an agent for...
              </Typography>
            </Box>
            <AutocompleteInputName
              options={intentOptionsFromRole}
              setForm={setForm}
              form={form}
              value={form.name}
              label={"Agent Intent"}
              placeholder="What does your agent do? What problem does it solve?"
            />
            
            {/* Display Name Section */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                Display Name (Optional)
              </Typography>
              <TextField
                label="Display Name"
                value={form.display_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, display_name: e.target.value }))
                }
                fullWidth
                placeholder="A user-friendly name for your agent (e.g., 'Sales Assistant', 'Data Analyzer')"
                helperText="If not provided, the agent intent will be used as the display name"
              />
            </Box>
            
            {/* Agent Description Section */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                Describe your agent
              </Typography>
              <TextField
                label="Agent Description"
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                fullWidth
                multiline
                minRows={4}
                placeholder="What does your agent do? What problem does it solve?"
              />
            </Box>
            {/* </Box> */}
          </Box>
        );

      case 2:
        const handleToolSelect = (tool: any) => {
          if (!selectedTools.some((t: any) => t.name === tool.name)) {
            setSelectedTools([...selectedTools, { ...tool, paramValues: {}, selectedKey: null }]);
          }
        };
        const handleToolRemove = (name: any) => {
          setSelectedTools(selectedTools.filter((t: any) => t.name !== name));
        };
        const handleKeySelect = (toolIdx: any, key: any) => {
          setSelectedTools((prev: any[]) =>
            prev.map((tool: any, i: any) =>
              i === toolIdx ? { ...tool, selectedKey: key?.name || null } : tool
            )
          );
        };
        // Tool editing logic for custom tools
        const handleEditTool = (idx: any, field: any, value: any) => {
          setSelectedTools((prev: any) => {
            // Use a new array/object for the tool being edited to avoid React focus issues
            const updated = prev.map((tool: any, i: any) =>
              i === idx ? { ...tool, [field]: value } : tool
            );
            return [...updated];
          });
        };
        const handleEditParam = (
          toolIdx: any,
          paramIdx: any,
          field: any,
          value: any
        ) => {
          setSelectedTools((prev: any) => {
            // Deep copy the tool and params to avoid focus loss and ensure state updates
            const updated = prev.map((tool: any, i: any) => {
              if (i !== toolIdx) return tool;
              const params = tool.params
                ? tool.params.map((param: any, j: any) =>
                    j === paramIdx ? { ...param, [field]: value } : { ...param }
                  )
                : [];
              return { ...tool, params: [...params] };
            });
            return [...updated];
          });
        };
        // Remove parameter
        const handleRemoveParam = (toolIdx: any, paramIdx: any) => {
          setSelectedTools((prev: any) => {
            const updated = prev.map((tool: any, i: any) => {
              if (i !== toolIdx) return tool;
              const params = tool.params
                ? tool.params.filter((_: any, j: any) => j !== paramIdx)
                : [];
              return { ...tool, params: [...params] };
            });
            return [...updated];
          });
        };

        return (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Role: <b>{form.role}</b>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Intent: <b>{form.name}</b>
              </Typography>
            </Box>

            {/* Tools from Tool Catalog - optional */}
            <Box sx={{ mb: 3 }}>
              <Button
                variant="text"
                onClick={() => setShowCatalog(!showCatalog)}
                startIcon={<Inventory2Icon />}
                endIcon={showCatalog ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: 'none', mb: showCatalog ? 2 : 0, fontWeight: 500 }}
              >
                {showCatalog ? 'Hide ' : 'Browse '}Catalog Tools
                {!showCatalog && (
                  <Box component="span" sx={{ ml: 1, display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                    {catalogTools.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        ({catalogTools.length} available)
                      </Typography>
                    )}
                    {selectedTools.filter((t: any) => t.tool_id).length > 0 && (
                      <Chip 
                        label={`${selectedTools.filter((t: any) => t.tool_id).length} selected`} 
                        size="small" 
                        color="success"
                        sx={{ height: 20, fontSize: '0.75rem' }} 
                      />
                    )}
                  </Box>
                )}
              </Button>
              <Collapse in={showCatalog}>
              
              {loadingTools ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading tools from catalog...
                  </Typography>
                </Box>
              ) : catalogTools.length > 0 ? (
                <Box>
                  {/* Search and Filter Controls */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      placeholder="Search tools..."
                      value={toolSearchQuery}
                      onChange={(e) => setToolSearchQuery(e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Autocomplete
                      options={['all', ...CATEGORY_OPTIONS.map((category) => category.id)]}
                      value={selectedCategory}
                      onChange={(_, value) => setSelectedCategory(value || 'all')}
                      getOptionLabel={(option) =>
                        option === 'all'
                          ? 'All categories'
                          : CATEGORY_OPTIONS.find((category) => category.id === option)?.label || String(option)
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Category" size="small" />
                      )}
                      sx={{ minWidth: 200 }}
                      disableClearable
                    />
                  </Box>

                  {/* Tool Cards Grid */}
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                    gap: 2,
                    mb: 2,
                    maxHeight: 400,
                    overflow: 'auto',
                  }}>
                    {catalogTools
                      .filter((tool: any) => {
                        // Filter by search query
                        const metadata = getToolCatalogMetadata(tool);
                        const matchesSearch = !toolSearchQuery || 
                          tool.name.toLowerCase().includes(toolSearchQuery.toLowerCase()) ||
                          tool.description.toLowerCase().includes(toolSearchQuery.toLowerCase()) ||
                          metadata.collection.toLowerCase().includes(toolSearchQuery.toLowerCase()) ||
                          metadata.canonicalCategory.label.toLowerCase().includes(toolSearchQuery.toLowerCase());
                        
                        // Filter by category
                        const matchesCategory = selectedCategory === 'all' || 
                          metadata.canonicalCategory.id === selectedCategory;
                        
                        return matchesSearch && matchesCategory;
                      })
                      .map((tool: any) => {
                        const metadata = getToolCatalogMetadata(tool);
                        const setupRequirements = metadata.setupRequirements;
                        const isSelected = selectedTools.some((t: any) => t.tool_id === tool.tool_id);
                        return (
                          <Card 
                            key={tool.tool_id}
                            variant="outlined" 
                            sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              borderColor: isSelected ? 'primary.main' : 'divider',
                              borderWidth: isSelected ? 2 : 1,
                              bgcolor: isSelected ? 'primary.50' : 'background.paper',
                            }}
                          >
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, fontSize: '0.875rem' }}>
                                  {tool.display_name}
                                </Typography>
                                <IconButton 
                                  size="small" 
                                  onClick={() => {
                                    setSelectedToolDetail(tool);
                                    setToolDetailOpen(true);
                                  }}
                                  sx={{ ml: 0.5, mt: -0.5, p: 0.5 }}
                                >
                                  <VisibilityIcon 
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '1.2rem',
                                    color: 'primary.main',
                                    '&:hover': {
                                      bgcolor: 'transparent',
                                      color: 'primary.dark',
                                    },
                                  }}
                                  />
                                </IconButton>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.7rem', lineHeight: 1.3 }}>
                                {tool.description.length > 70 
                                  ? `${tool.description.substring(0, 70)}...` 
                                  : tool.description}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Chip 
                                  label={metadata.canonicalCategory.label}
                                  size="small" 
                                  sx={{ fontSize: '0.625rem', height: 18 }}
                                />
                                {metadata.providerLabel && (
                                  <Chip
                                    label={metadata.providerLabel}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.625rem', height: 18 }}
                                  />
                                )}
                                {setupRequirements.slice(0, 1).map((requirement) => (
                                  <Chip
                                    key={requirement.id}
                                    label={getRequirementLabel(requirement)}
                                    size="small"
                                    sx={{
                                      fontSize: '0.625rem',
                                      height: 18,
                                      bgcolor: requirement.satisfied ? '#eef7ee' : '#fff4e8',
                                      color: requirement.satisfied ? '#2f6f35' : '#8f5726',
                                      fontWeight: 700,
                                    }}
                                  />
                                ))}
                                {tool.params.length > 0 && (
                                  <Chip 
                                    label={`${tool.params.length} param${tool.params.length !== 1 ? 's' : ''}`} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ fontSize: '0.625rem', height: 18 }}
                                  />
                                )}
                                <Box sx={{ flex: 1 }} />
                                <Button
                                  size="small"
                                  variant={isSelected ? "contained" : "outlined"}
                                  onClick={() => handleToolSelect(tool)}
                                  disabled={isSelected}
                                  sx={{ 
                                    textTransform: 'none', 
                                    fontSize: '0.7rem',
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.25,
                                    height: 22,
                                  }}
                                >
                                  {isSelected ? '✓' : 'Add'}
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </Box>
                  
                  {catalogTools.filter((tool: any) => {
                    const metadata = getToolCatalogMetadata(tool);
                    const matchesSearch = !toolSearchQuery || 
                      tool.name.toLowerCase().includes(toolSearchQuery.toLowerCase()) ||
                      tool.description.toLowerCase().includes(toolSearchQuery.toLowerCase()) ||
                      metadata.collection.toLowerCase().includes(toolSearchQuery.toLowerCase()) ||
                      metadata.canonicalCategory.label.toLowerCase().includes(toolSearchQuery.toLowerCase());
                    const matchesCategory = selectedCategory === 'all' || 
                      metadata.canonicalCategory.id === selectedCategory;
                    return matchesSearch && matchesCategory;
                  }).length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No tools found matching your search criteria.
                    </Alert>
                  )}
                </Box>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No active tools available in the catalog yet.
                </Alert>
              )}
              </Collapse>
            </Box>

            {selectedTools.length > 0 && (
              <Box sx={{ my: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  Selected Tools ({selectedTools.length})
                </Typography>
                {selectedTools.map((tool: any, idx: any) => {
                  const isCatalogTool = !!tool.tool_id; // Tools from catalog have tool_id
                  const metadata = getToolCatalogMetadata(tool);
                  const setupRequirements = metadata.setupRequirements;
                  
                  return (
                    <Box
                      key={tool.tool_id || tool.name || idx}
                      sx={{
                        mb: 2,
                        p: 2,
                        borderLeft: isCatalogTool ? "3px solid #B87333" : "2px solid #eee",
                        bgcolor: isCatalogTool ? "#fffaf5" : "transparent",
                        borderRadius: 1,
                      }}
                    >
                      {isCatalogTool ? (
                        // Read-only display for catalog tools
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {tool.name}
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              px: 1, 
                              py: 0.25, 
                              bgcolor: '#B87333',
                              color: 'white', 
                              borderRadius: 1,
                              fontSize: '0.65rem'
                            }}>
                              FROM CATALOG
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              v{tool.version}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <Button
                              variant="text"
                              size="small"
                              color="error"
                              onClick={() => handleToolRemove(tool.name)}
                            >
                              Remove
                            </Button>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {tool.description}
                          </Typography>

                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                            <Chip
                              label={metadata.collection}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: "0.7rem", borderColor: "#e2cab5" }}
                            />
                            <Chip
                              label={metadata.canonicalCategory.label}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: "0.7rem", borderColor: "#e2cab5" }}
                            />
                            {setupRequirements.length === 0 ? (
                              <Chip
                                label="No external setup"
                                size="small"
                                sx={{ height: 22, fontSize: "0.7rem", bgcolor: "#eef7ee", color: "#2f6f35", fontWeight: 700 }}
                              />
                            ) : (
                              setupRequirements.map((requirement) => (
                                <Chip
                                  key={requirement.id}
                                  label={getRequirementLabel(requirement)}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: "0.7rem",
                                    bgcolor: requirement.satisfied ? "#eef7ee" : "#fff4e8",
                                    color: requirement.satisfied ? "#2f6f35" : "#8f5726",
                                    border: "1px solid",
                                    borderColor: requirement.satisfied ? "#cce6cf" : "#efd0af",
                                    fontWeight: 700,
                                  }}
                                />
                              ))
                            )}
                          </Stack>
                          
                          
                          {/* Tool credentials are selected per agent so the same tool can use different keys in different agents. */}
                          {Array.isArray(tool.keys_schema) && tool.keys_schema.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Autocomplete
                                options={availableKeys}
                                getOptionLabel={(option) => option?.display_name || option?.name || ""}
                                value={availableKeys.find((key: any) => key.name === tool.selectedKey) || null}
                                onChange={(_, value) => handleKeySelect(idx, value)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={`Select API Key for ${tool.keys_schema[0]?.name || "tool"}`}
                                    size="small"
                                    required
                                    helperText="Choose a key from Key Manager for this tool"
                                  />
                                )}
                                sx={{ maxWidth: 420 }}
                              />
                            </Box>
                          )}

                          {tool.params && tool.params.length > 0 && (
                            <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                Parameters:
                              </Typography>
                              {tool.params.map((param: any, paramIdx: any) => (
                                <Typography key={paramIdx} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  • {param.name} <em>({param.type})</em>{param.required && <strong> *required</strong>}
                                  {param.description && ` - ${param.description}`}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      ) : (
                        // Editable display for custom tools
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-end",
                              mb: 1,
                              gap: 2,
                            }}
                          >
                            <Autocomplete
                              freeSolo
                              options={[]}
                              value={tool.name}
                              onInputChange={(_, value) =>
                                handleEditTool(idx, "name", value)
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Tool Name"
                                  size="small"
                                />
                              )}
                              sx={{ width: "22%" }}
                            />
                            <Autocomplete
                              freeSolo
                              options={[]}
                              value={tool.description}
                              onInputChange={(_, value) =>
                                handleEditTool(idx, "description", value)
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Description"
                                  size="small"
                                />
                              )}
                              sx={{ width: "45%" }}
                            />
                            <Button
                              variant="text"
                              size="small"
                              color="error"
                              onClick={() => handleToolRemove(tool.name)}
                            >
                              Remove
                            </Button>
                          </Box>
                          
                          {tool.params && tool.params.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                Parameters:
                              </Typography>
                              {tool.params.map((param: any, paramIdx: any) => (
                                <Box
                                  key={paramIdx}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: 1,
                                  }}
                                >
                                  <TextField
                                    label="Name"
                                    value={param.name}
                                    onChange={(e) =>
                                      handleEditParam(
                                        idx,
                                        paramIdx,
                                        "name",
                                        e.target.value
                                      )
                                    }
                                    size="small"
                                    sx={{ mr: 1, width: "25%" }}
                                  />
                                  <Autocomplete
                                    options={["string", "number", "boolean", "json"]}
                                    value={param.type}
                                    onChange={(_, value) =>
                                      handleEditParam(idx, paramIdx, "type", value)
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Type"
                                        size="small"
                                      />
                                    )}
                                    sx={{ mr: 1, width: "20%" }}
                                  />
                                  <TextField
                                    label="Description"
                                    value={param.description}
                                    onChange={(e) =>
                                      handleEditParam(
                                        idx,
                                        paramIdx,
                                        "description",
                                        e.target.value
                                      )
                                    }
                                    size="small"
                                    sx={{ mr: 1, width: "45%" }}
                                  />
                                  <Button
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveParam(idx, paramIdx)}
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            {selectedTools.some(
              (tool: any) =>
                Array.isArray(tool.keys_schema) &&
                tool.keys_schema.length > 0 &&
                !tool.selectedKey
            ) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Please select an API key for all tools before continuing to the next step.
              </Alert>
            )}
            
            {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
            {/* ****************************************************** */}

            {/* Tool Detail Modal */}
            <Dialog 
              open={toolDetailOpen} 
              onClose={() => setToolDetailOpen(false)}
              maxWidth="md"
              fullWidth
            >
              {selectedToolDetail && (
                <>
                  <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" component="span" sx={{ flex: 1 }}>
                        {selectedToolDetail.name}
                      </Typography>
                      <Chip 
                        label={`v${selectedToolDetail.version}`} 
                        size="small" 
                      />
                    </Box>
                  </DialogTitle>
                  <DialogContent dividers>
                    {(() => {
                      const metadata = getToolCatalogMetadata(selectedToolDetail);
                      const setupRequirements = metadata.setupRequirements;
                      return (
                        <>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {selectedToolDetail.description}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: "wrap" }}>
                        <Chip 
                          label={metadata.collection}
                          variant="outlined"
                          size="small"
                          sx={{ borderColor: "#e2cab5" }}
                        />
                        <Chip
                          label={metadata.canonicalCategory.label}
                          variant="outlined"
                          size="small"
                          sx={{ borderColor: "#e2cab5" }}
                        />
                        {metadata.providerLabel && (
                          <Chip
                            label={metadata.providerLabel}
                            variant="outlined"
                            size="small"
                            sx={{ borderColor: "#e2cab5" }}
                          />
                        )}
                      </Box>

                      <Box sx={{ p: 1.5, border: "1px solid #ead9c9", borderRadius: 2, bgcolor: "#fffaf5" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          Setup requirements
                        </Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {setupRequirements.length === 0 ? (
                            <Chip
                              label="No external setup required"
                              size="small"
                              sx={{ bgcolor: "#eef7ee", color: "#2f6f35", fontWeight: 700 }}
                            />
                          ) : (
                            setupRequirements.map((requirement) => (
                              <Chip
                                key={requirement.id}
                                label={getRequirementLabel(requirement)}
                                size="small"
                                sx={{
                                  bgcolor: requirement.satisfied ? "#eef7ee" : "#fff4e8",
                                  color: requirement.satisfied ? "#2f6f35" : "#8f5726",
                                  border: "1px solid",
                                  borderColor: requirement.satisfied ? "#cce6cf" : "#efd0af",
                                  fontWeight: 700,
                                }}
                              />
                            ))
                          )}
                        </Stack>
                      </Box>
                    </Box>
                        </>
                      );
                    })()}

                    {selectedToolDetail.params && selectedToolDetail.params.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Parameters ({selectedToolDetail.params.length})
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                          {selectedToolDetail.params.map((param: any, idx: number) => (
                            <Box key={idx} sx={{ mb: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {param.name}
                                </Typography>
                                <Chip 
                                  label={param.type} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                                {param.required && (
                                  <Chip 
                                    label="required" 
                                    size="small" 
                                    color="error"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Box>
                              {param.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {param.description}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {selectedToolDetail.return_value && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Return Value
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '3px solid', borderColor: 'success.main' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Chip 
                              label={selectedToolDetail.return_value.type || 'any'} 
                              size="small" 
                              color="success"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          </Box>
                          {selectedToolDetail.return_value.description && (
                            <Typography variant="caption" color="text.secondary">
                              {selectedToolDetail.return_value.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setToolDetailOpen(false)}>
                      Close
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={() => {
                        handleToolSelect(selectedToolDetail);
                        setToolDetailOpen(false);
                      }}
                      disabled={selectedTools.some((t: any) => t.tool_id === selectedToolDetail.tool_id)}
                    >
                      {selectedTools.some((t: any) => t.tool_id === selectedToolDetail.tool_id) 
                        ? '✓ Already Added' 
                        : 'Add to Agent'}
                    </Button>
                  </DialogActions>
                </>
              )}
            </Dialog>

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
            ></Box>
          </Box>
        );

      // Step 3: LLM settings
      case 3:
        // Sync manual JSON and form values
        const handleManualSwitch = (checked: any) => {
          setManualMode(checked);
          if (checked) {
            // Populate manual JSON from form
            setManualJsonValue(
              JSON.stringify(
                {
                  llm: form.llm,
                  temperature: form.temperature,
                  max_tokens: form.max_tokens,
                },
                null,
                2
              )
            );
          } else {
            // Populate form from manual JSON
            try {
              const json = JSON.parse(manualJsonValue);
              setForm((f) => ({
                ...f,
                llm: json.llm || "openai",
                temperature:
                  typeof json.temperature === "number" ? json.temperature : 0,
                max_tokens:
                  typeof json.max_tokens === "number" ? json.max_tokens : 4096,
              }));
            } catch (e) {
              // Ignore parse errors
            }
          }
        };
        return (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Role: <b>{form.role}</b>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Intent: <b>{form.name}</b>
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={manualMode}
                    onChange={(e) => handleManualSwitch(e.target.checked)}
                  />
                }
                label="Manual JSON mode"
              />
            </Box>
            {!manualMode ? (
              <Box>
                <Autocomplete
                  options={llmOptions}
                  value={form.llm}
                  onChange={(_, value) =>
                    setForm((f) => ({ ...f, llm: value || "openai" }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="LLM Provider" />
                  )}
                  sx={{ minWidth: 220, mb: 2 }}
                  disableClearable
                />
                <TextField
                  label="Temperature"
                  type="number"
                  value={form.temperature}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      temperature: parseFloat(e.target.value),
                    }))
                  }
                  sx={{ mb: 2 }}
                  fullWidth
                />
                <TextField
                  label="Max Tokens"
                  type="number"
                  value={form.max_tokens}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      max_tokens: parseInt(e.target.value),
                    }))
                  }
                  sx={{ mb: 2 }}
                  fullWidth
                />
              </Box>
            ) : (
              <TextField
                label="LLM Settings JSON"
                multiline
                minRows={8}
                value={manualJsonValue}
                onChange={(e) => setManualJsonValue(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
            )}
            {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
            ></Box>
          </Box>
        );

      case 4:
        return (
          <Box>
            {!isEdit &&
              <Alert severity="success" sx={{ mb: 3 }}>
                
                <Typography variant="body2">
                  <strong>You're all set!</strong> Review your configuration
                  below. You can always edit these settings later.
                </Typography>
              </Alert>
            }
            <Box
              // component={Paper}
              // elevation={3}
              sx={{ maxWidth: 1080, mx: "auto", my: 2, p: 1 }}
            >
              {/* <Typography variant="h5" gutterBottom>{isEdit ? "Review & Update Agent" : "Review & Create Agent"}</Typography> */}
              <Box sx={{ mb: 2 }}>
                {isEdit && (
                  <Typography variant="body2" color="text.secondary">
                    Agent ID: <b>{editingAgentCard.agent_id}</b>
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Role: <b>{form.role}</b>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Intent: <b>{form.name}</b>
                </Typography>
              </Box>
              {/* Agent Description */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  Agent Description
                </Typography>
                <Typography variant="body2">{form.description}</Typography>
              </Box>
              {/* Tools Review */}
              {reviewTools.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                    Selected Tools
                  </Typography>
                  {reviewTools.map((tool: any) => {
                    const toolDisplayName = tool.display_name || tool.displayName || tool.name;
                    return (
                    <Box
                      key={tool.tool_id || tool.name}
                      sx={{ mb: 1, pl: 2, borderLeft: "2px solid #eee" }}
                    >
                      {(() => {
                        const metadata = getToolCatalogMetadata(tool);
                        const setupRequirements = metadata.setupRequirements;
                        return (
                          <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap sx={{ mb: 0.7 }}>
                            <Chip label={metadata.collection} size="small" variant="outlined" sx={{ height: 22, borderColor: "#e2cab5" }} />
                            <Chip label={metadata.canonicalCategory.label} size="small" variant="outlined" sx={{ height: 22, borderColor: "#e2cab5" }} />
                            {setupRequirements.length === 0 ? (
                              <Chip label="No external setup" size="small" sx={{ height: 22, bgcolor: "#eef7ee", color: "#2f6f35", fontWeight: 700 }} />
                            ) : (
                              setupRequirements.slice(0, 3).map((requirement) => (
                                <Chip
                                  key={requirement.id}
                                  label={getRequirementLabel(requirement)}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    bgcolor: requirement.satisfied ? "#eef7ee" : "#fff4e8",
                                    color: requirement.satisfied ? "#2f6f35" : "#8f5726",
                                    border: "1px solid",
                                    borderColor: requirement.satisfied ? "#cce6cf" : "#efd0af",
                                    fontWeight: 700,
                                  }}
                                />
                              ))
                            )}
                          </Stack>
                        );
                      })()}
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <b>{toolDisplayName}</b>
                        {tool.name && tool.name !== toolDisplayName && (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {" "}({tool.name})
                          </Typography>
                        )}
                        {tool.description ? ` - ${tool.description}` : ""}
                      </Typography>
                      {tool.parameters && tool.parameters.length > 0 && (
                        <Box sx={{ pl: 2 }}>
                          {tool.parameters.map((param: any) => (
                            <Typography
                              key={param.name}
                              variant="caption"
                              color="text.secondary"
                            >
                              {param.name} ({param.type}): {param.description}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                    );
                  })}
                </Box>
              )}
              {/* LLM Settings Review */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  LLM Settings
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Provider: <b>{form.llm}</b>
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Temperature: <b>{form.temperature}</b>
                </Typography>
                <Typography variant="body2">
                  Max Tokens: <b>{form.max_tokens}</b>
                </Typography>
              </Box>
              {/* Associate Indexes */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  Knowledge Collection (optional)
                </Typography>
                <Autocomplete
                  multiple
                  options={docIndexes}
                  getOptionLabel={(opt: any) => opt.name || opt.index_id}
                  value={docIndexes.filter((i: any) => selectedKnowledgeSpaceIds.includes(i.index_id))}
                  onChange={(_, values) => {
                    const nextIds = values.map((value: any) => value.index_id).filter(Boolean);
                    setSelectedKnowledgeSpaceIds(nextIds);
                    setSelectedDocIndex(nextIds[0] || null);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Knowledge Spaces" helperText="Attach one or more governed Knowledge Spaces. Agents query them through query_knowledge." />
                  )}
                  sx={{ mb: 2 }}
                />
                
              </Box>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };
  
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Page Header - Fixed */}
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Typography variant="h5" fontWeight="600" sx={{ mb: 0.5 }}>
          {isEdit ? "Edit Agent" : "Create Agent"}
        </Typography>
        {!isEdit && 
        <Typography variant="body2" color="text.secondary">
          Follow the steps below to configure your new agent
        </Typography>
  }
      </Box>

      {/* Scrollable Content Area */}
      <Box sx={{ flex: 1, overflow: "auto", px: 3 }}>
        <Paper
          elevation={2}
          sx={{ pb: 3, pl: 3, pr: 3, pt: 1, mb: 3, maxWidth: 1080 }}
        >
          {/* Stepper - Compact */}
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((step, index) => {
              // For non-admin in edit mode, mark steps 0-3 as completed and disabled
              // For admin or create mode, use the normal completedSteps state
              const isStepDisabled = isEdit && isNonAdmin && index < 4;
              const isStepCompleted = isEdit && isNonAdmin && index < 4 ? true : completedSteps[index];
              
              return (
                <Step key={step.label} completed={isStepCompleted} disabled={isStepDisabled}>
                  <StepLabel 
                    sx={{
                      ...(isStepDisabled && {
                        '& .MuiStepLabel-label': {
                          color: 'text.disabled',
                          opacity: 0.6
                        },
                        '& .MuiStepIcon-root': {
                          color: 'action.disabled',
                          opacity: 0.6
                        }
                      })
                    }}
                  >
                    {step.label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>

          {/* Step Header with Guidance - Compact */}
          <Box sx={{ mb: 0.5 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography variant="h6" fontWeight="600">
                {currentStepConfig.title}
              </Typography>
              {currentStepConfig.helpText && (
                <Tooltip title={currentStepConfig.helpText} arrow>
                  <IconButton size="small" color="primary">
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {currentStepConfig.description || ""}
            </Typography>
            {currentStepConfig.tipText && (
              <Alert severity="info" icon={false} sx={{ pt: 0.5 }}>
                <Typography variant="body2">
                  {currentStepConfig.tipText}
                </Typography>
              </Alert>
            )}
          </Box>

          {/* Step Content - Scrollable if needed */}
          <Box sx={{ mb: 2 }}>{renderStepContent()}</Box>

          {/* Navigation - Fixed at bottom of card */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              pt: 2,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0 || (isEdit && isNonAdmin)}
              startIcon={<NavigateBeforeIcon />}
              sx={{ borderRadius: "99px" }}
            >
              Back
            </Button>
            {/* Confirmation & Create/Update Button */}
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateAgent}
                disabled={loading}
                sx={{ width: "100%", maxWidth: 300 }}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : isEdit ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<NavigateNextIcon />}
                disabled={!isCurrentStepComplete()} // ← Disabled based on validation
                sx={{ borderRadius: "99px" }}
              >
                Continue
              </Button>
            )}
          </Box>
          {successMsg && (
            <Box sx={{ mt: 2 }}>
              <Typography color="success.main" sx={{ mb: 1 }}>
                <span dangerouslySetInnerHTML={{ __html: successMsg }} />
              </Typography>
            </Box>
          )}
          {success && (
            <Box sx={{ mt: 3 }}>
              <Typography color="success.main" sx={{ mb: 1 }}>
                <span dangerouslySetInnerHTML={{ __html: success }} />
              </Typography>
            </Box>
          )}
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default StepperWizard;
